// src/services/projectService.js
import api from './api';

const BILLING_LOOKUP_CACHE_TTL_MS = 60 * 1000;
const billingLookupCache = new Map();

const getCacheEntry = (cacheStore, key) => {
  const entry = cacheStore.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    cacheStore.delete(key);
    return null;
  }
  return entry.value;
};

const setCacheEntry = (cacheStore, key, value, ttlMs) => {
  cacheStore.set(key, {
    value,
    expiresAt: Date.now() + ttlMs
  });
};

const clearBillingLookupCache = () => {
  billingLookupCache.clear();
};

const buildBillingLookupCacheKey = (params = {}) => JSON.stringify({
  lookup: String(params.lookup || '').trim().toLowerCase(),
  q: String(params.q || '').trim().toLowerCase(),
  selected_id: Number(params.selected_id) || 0,
  limit: Number(params.limit) || 0
});

const projectService = {
  // Get all projects
  getProjects: async (params = {}) => {
    const isBillingLookup = String(params.lookup || '').trim().toLowerCase() === 'billing_form';
    const cacheKey = isBillingLookup ? buildBillingLookupCacheKey(params) : null;
    if (cacheKey) {
      const cached = getCacheEntry(billingLookupCache, cacheKey);
      if (cached) return cached;
    }

    const response = await api.get('/projects', { params });
    if (cacheKey) {
      setCacheEntry(billingLookupCache, cacheKey, response.data, BILLING_LOOKUP_CACHE_TTL_MS);
    }
    return response.data;
  },

  // Get single project
  getProjectById: async (id) => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  // Get project workspace (project + related records)
  getProjectWorkspace: async (id) => {
    const response = await api.get(`/projects/${id}/workspace`);
    return response.data;
  },

  // Project followups (daily/weekly check-ins)
  getProjectFollowups: async (id, params = {}) => {
    const response = await api.get(`/projects/${id}/followups`, { params });
    return response.data;
  },

  exportProjectFollowupsExcel: async (id, params = {}) => {
    const response = await api.get(`/projects/${id}/followups/export/excel`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  createProjectFollowup: async (id, payload) => {
    const response = await api.post(`/projects/${id}/followups`, payload);
    return response.data;
  },

  updateProjectFollowup: async (id, followupId, payload) => {
    const response = await api.put(`/projects/${id}/followups/${followupId}`, payload);
    return response.data;
  },

  deleteProjectFollowup: async (id, followupId) => {
    const response = await api.delete(`/projects/${id}/followups/${followupId}`);
    return response.data;
  },

  // Create project
  createProject: async (projectData) => {
    const response = await api.post('/projects', projectData);
    clearBillingLookupCache();
    return response.data;
  },

  // Update project
  updateProject: async (id, projectData) => {
    const response = await api.put(`/projects/${id}`, projectData);
    clearBillingLookupCache();
    return response.data;
  },

  // Delete project
  deleteProject: async (id) => {
    const response = await api.delete(`/projects/${id}`);
    clearBillingLookupCache();
    return response.data;
  },
};

export default projectService;
